import { applyMiddleware, legacy_createStore as createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import history from 'history/browser';
import { initialize } from 'ext/root/root-actions';
import getReduxConfig from 'ext/lib/redux-config';
import { create as createLoggerClient } from 'ext/lib/loggers';
import { createApi, apiClient as createApiClient } from 'ext/lib/api';
import { isAuth0Enabled, getToken } from 'ext/lib/auth/web';
import { parseSelected } from 'lib/location';
import { getUserPreferences } from 'lib/user-preferences';

export default async function configureStore({
  auth = {},
  user = {},
  endpointOverrides,
  studioUrl,
  region,
  rootReducer,
  rootSaga,
}) {
  // Create configurations for Redux via Reactotron
  const { composeEnhancers, sagaMonitor, logStateChange } =
    await getReduxConfig();

  // Create API client (mobile-builder is always standalone)
  let api;
  if (await isAuth0Enabled()) {
    // Auth0 mode: use apiClient with token provider for fresh tokens on each request
    const client = createApiClient({
      getToken,
      getAccountId: () => auth.accountId,
      getEndpointOverrides: () => endpointOverrides,
    });
    api = await client.getEndpoints();
  } else {
    // Legacy mode: use createApi with static token
    api = await createApi({
      jwt: auth.jwt,
      accountId: auth.accountId,
      standalone: true,
    });
  }

  // Create saga middleware with context
  const sagaMiddleware = createSagaMiddleware({
    sagaMonitor,
    context: {
      history,
      api,
      logger: createLoggerClient({
        account: {
          accountId: auth.accountId,
          userId: auth.userId,
        },
      }),
    },
  });

  // Generate initial state based on URL
  const { pathname, hash } = history.location;

  // Create initial data before creating store
  const state = {
    account: {
      ...auth,
      accountId: auth.accountId,
      userId: auth.userId,
      email: user.email,
      isSpoofing: user.isSpoofing,
      studioUrl: studioUrl || import.meta.env.STUDIO_URL,
      region,
    },
    selected: parseSelected({ pathname, hash }),
    userPreferences: getUserPreferences(),
  };

  // Create enhancers for Redux dispatch
  const enhancers = composeEnhancers(applyMiddleware(sagaMiddleware));

  // Create middleware enhanced store
  const store = createStore(rootReducer, state, enhancers);

  // Run the root saga
  sagaMiddleware.run(rootSaga);

  // Dispatch initialize action to kick off any start up tasks
  store.dispatch(initialize(auth));

  // Log state change to Reactotron
  logStateChange(store, 'Mobile builder');

  // Return configured Redux store and apiClient
  return { store, apiClient: api };
}
